import React, { useState, useMemo, useEffect } from 'react'
import {
  Card,
  Row,
  Col,
  Select,
  Button,
  Table,
  Tag,
  Badge,
  Collapse,
  Space,
  Typography,
  Alert,
  Spin,
  Tooltip,
  Empty,
  Statistic
} from 'antd'
import {
  SyncOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  CarOutlined,
  BarcodeOutlined,
  FileTextOutlined,
  EnvironmentOutlined,
  SearchOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined
} from '@ant-design/icons'
import { notaanota } from '../../../services/notaanota'
import { useAuth } from '../../../contexts/AuthContext'

const { Text, Title } = Typography

// Ordenação crescente de CTRC (trata números e textos de forma natural, posicionando 'Sem CTRC' ao final)
const compararCtrc = (a, b) => {
  const ctrcA = String(a?.ctrc || '').trim()
  const ctrcB = String(b?.ctrc || '').trim()
  const isSemCtrcA = !ctrcA || ctrcA.toUpperCase().includes('SEM CTRC')
  const isSemCtrcB = !ctrcB || ctrcB.toUpperCase().includes('SEM CTRC')

  if (isSemCtrcA && !isSemCtrcB) return 1
  if (!isSemCtrcA && isSemCtrcB) return -1

  return ctrcA.localeCompare(ctrcB, undefined, { numeric: true, sensitivity: 'base' })
}

const ConferenciaExpedicaoTab = ({
  dadosExpedicao = [],
  selectedDate,
  empresa,
  dominio,
  currentTheme = 'light',
  isMobile = false
}) => {
  const { user } = useAuth()
  const [carroSelecionado, setCarroSelecionado] = useState(null)
  const [loadingSsw, setLoadingSsw] = useState(false)
  const [dadosSsw, setDadosSsw] = useState(null)
  const [erroSsw, setErroSsw] = useState(null)
  const [activeClientPanels, setActiveClientPanels] = useState([])
  const [activeCtrcsByClient, setActiveCtrcsByClient] = useState({})
  const [isFullscreen, setIsFullscreen] = useState(false)

  const isDark = currentTheme === 'dark'

  // Resolução resiliente de domínio para o ERP
  const finalDominio = useMemo(() => {
    if (dominio) return dominio
    try {
      const erpSession = JSON.parse(localStorage.getItem('erpSession') || 'null')
      if (erpSession?.dominio) return erpSession.dominio
      const loginRom = JSON.parse(localStorage.getItem('loginRomaneioData') || 'null')
      if (loginRom?.dominio) return loginRom.dominio
    } catch { }
    return (
      user?.organization?.dominio ||
      user?.organization?.domain ||
      user?.erpDominio ||
      localStorage.getItem('login.dominio') ||
      ''
    )
  }, [dominio, user])

  // Listener para sair do modo tela cheia ao pressionar Esc e travar scroll de fundo
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false)
      }
    }
    if (isFullscreen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [isFullscreen])

  // Lista única de veículos do dia presentes nos dados locais
  const listaVeiculos = useMemo(() => {
    const set = new Set()
    dadosExpedicao.forEach(item => {
      const c = (item.CARRO || item.carro || '').trim()
      if (c && c !== 'SEM CARRO') set.add(c)
    })
    return Array.from(set).sort()
  }, [dadosExpedicao])

  // Veículos com ocorrências no sistema (tabela nota_veiculo_usos)
  const veiculosComOcorrencias = useMemo(() => {
    const mapa = new Map()

    dadosExpedicao.forEach(item => {
      const placa = (item.CARRO || item.carro || '').trim().toUpperCase()
      if (!placa || placa === 'SEM CARRO') return

      if (!mapa.has(placa)) {
        mapa.set(placa, {
          placa,
          totalNotas: 0,
          totalVolumes: 0,
          totalImpressas: 0,
          totalPendentes: 0
        })
      }

      const obj = mapa.get(placa)
      obj.totalNotas += 1
      obj.totalVolumes += Number(item.VOLUMES || item.volumes || 0)
      if (Number(item.STATUS) === 1) {
        obj.totalImpressas += 1
      } else {
        obj.totalPendentes += 1
      }
    })

    return Array.from(mapa.values()).sort((a, b) => b.totalNotas - a.totalNotas)
  }, [dadosExpedicao])

  // Filtrar notas do veículo selecionado
  const notasDoCarro = useMemo(() => {
    if (!carroSelecionado) return []
    return dadosExpedicao.filter(
      item => (item.CARRO || item.carro || '').trim().toUpperCase() === carroSelecionado.toUpperCase()
    )
  }, [dadosExpedicao, carroSelecionado])

  // Mapas de lookup do ERP SSW para confronto
  const lookupSswPorNota = useMemo(() => {
    const map = new Map()
    if (!dadosSsw || !Array.isArray(dadosSsw.itens)) return map

    dadosSsw.itens.forEach(item => {
      const notaLimpa = String(item.nota || '').replace(/^0+/, '')
      const key = `${notaLimpa}`
      if (!map.has(key)) {
        map.set(key, [])
      }
      map.get(key).push(item)
    })
    return map
  }, [dadosSsw])

  // Agrupar dados locais em: Cliente (Remetente) -> CTRC -> Notas Fiscais
  const estruturaHierarquica = useMemo(() => {
    const clientesMap = new Map()

    notasDoCarro.forEach(item => {
      const clienteNome = (item.REMETENTE || item.CLIENTE || item.cliente || 'CLIENTE NÃO IDENTIFICADO').trim()
      const clienteCnpj = (item.CNPJ || item.cnpj || '').trim()
      const ctrcCodigo = (item.ctrc || item.cte || item.CTRC || item.CTE || 'SEM CTRC').trim()

      if (!clientesMap.has(clienteNome)) {
        clientesMap.set(clienteNome, {
          cliente: clienteNome,
          cnpj: clienteCnpj,
          ctrcs: new Map()
        })
      }

      const clienteObj = clientesMap.get(clienteNome)
      if (!clienteObj.ctrcs.has(ctrcCodigo)) {
        clienteObj.ctrcs.set(ctrcCodigo, {
          ctrc: ctrcCodigo,
          notas: []
        })
      }

      // Confronto com dados do ERP SSW (por CTRC + Nota, com fallback por Nota)
      const notaLimpa = String(item.NOTA || item.nota || '').replace(/^0+/, '')
      const ctrcLimpo = String(ctrcCodigo || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase()
      const sswItensCorrespondentes = lookupSswPorNota.get(notaLimpa) || []
      const sswItem =
        (ctrcLimpo &&
          sswItensCorrespondentes.find(
            s => String(s.ctrc || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase() === ctrcLimpo
          )) ||
        sswItensCorrespondentes[0] ||
        null

      const volumesLocal = Number(item.VOLUMES || item.volumes || 0)
      const volumesSsw = sswItem ? Number(sswItem.volumes || 0) : null

      const pesoLocal = Number(item.PESO ?? item.peso ?? 0)
      const pesoSsw =
        sswItem && sswItem.peso !== undefined && sswItem.peso !== null
          ? Number(sswItem.peso)
          : (sswItem && sswItem.PESO !== undefined && sswItem.PESO !== null ? Number(sswItem.PESO) : null)

      let statusConferencia = 'OK'
      let statusMotivo = 'Volumes e dados conferem'
      let statusColor = 'success'

      if (dadosSsw) {
        if (!sswItem) {
          statusConferencia = 'NÃO CONSTA NO ERP'
          statusMotivo = 'Nota não encontrada na relação do ERP'
          statusColor = 'warning'
        } else if (volumesSsw !== null && volumesSsw !== volumesLocal) {
          statusConferencia = 'DIVERGÊNCIA VOLUME'
          statusMotivo = `Volumes divergentes (ERP: ${volumesSsw} vs App: ${volumesLocal})`
          statusColor = 'error'
        } else if (pesoSsw !== null && Math.abs(pesoSsw - pesoLocal) > 0.01) {
          statusConferencia = 'DIVERGÊNCIA PESO'
          statusMotivo = `Pesos divergentes (ERP: ${pesoSsw.toFixed(2)} kg vs App: ${pesoLocal.toFixed(2)} kg)`
          statusColor = 'error'
        } else {
          statusConferencia = 'CONFERIDO'
          statusMotivo = 'Conferido com sucesso com o ERP SSW'
          statusColor = 'success'
        }
      } else {
        statusConferencia = Number(item.STATUS) === 1 ? 'ETIQUETADO' : 'PENDENTE'
        statusMotivo = Number(item.STATUS) === 1 ? 'Etiqueta impressa pelo motorista' : 'Pendente de etiquetagem'
        statusColor = Number(item.STATUS) === 1 ? 'processing' : 'default'
      }

      clienteObj.ctrcs.get(ctrcCodigo).notas.push({
        ...item,
        notaNumero: notaLimpa,
        volumesLocal,
        volumesSsw,
        pesoLocal,
        pesoSsw,
        sswItem,
        statusConferencia,
        statusMotivo,
        statusColor
      })
    })

    // Converter Maps para arrays e calcular somatórios
    return Array.from(clientesMap.values()).map(cli => {
      const ctrcsArray = Array.from(cli.ctrcs.values())
        .map(c => {
          const totalVolumesCtrc = c.notas.reduce((acc, n) => acc + (n.volumesLocal || 0), 0)
          const totalPesoCtrc = c.notas.reduce((acc, n) => acc + (n.pesoLocal || 0), 0)
          const totalM3Ctrc = c.notas.reduce((acc, n) => acc + (Number(n.M3 || n.m3 || 0) || 0), 0)
          return {
            ...c,
            totalVolumesCtrc,
            totalPesoCtrc,
            totalM3Ctrc
          }
        })
        .sort(compararCtrc)

      const totalVolumesCliente = ctrcsArray.reduce((acc, c) => acc + c.totalVolumesCtrc, 0)
      const totalPesoCliente = ctrcsArray.reduce((acc, c) => acc + c.totalPesoCtrc, 0)
      const totalNotasCliente = ctrcsArray.reduce((acc, c) => acc + c.notas.length, 0)

      return {
        ...cli,
        ctrcs: ctrcsArray,
        totalVolumesCliente,
        totalPesoCliente,
        totalNotasCliente
      }
    })
  }, [notasDoCarro, lookupSswPorNota, dadosSsw])

  // CTRCs/Notas do SSW que NÃO constam no nosso banco para este veículo
  const itensSswAusentesNoApp = useMemo(() => {
    if (!dadosSsw || !Array.isArray(dadosSsw.itens)) return []

    const notasNoAppSet = new Set(
      notasDoCarro.map(item => String(item.NOTA || item.nota || '').replace(/^0+/, ''))
    )

    return dadosSsw.itens.filter(sswIt => {
      const notaLimpa = String(sswIt.nota || '').replace(/^0+/, '')
      return !notasNoAppSet.has(notaLimpa)
    })
  }, [dadosSsw, notasDoCarro])

  // Estatísticas gerais
  const statsLocal = useMemo(() => {
    const totalNotas = notasDoCarro.length
    const totalVolumes = notasDoCarro.reduce((acc, item) => acc + Number(item.VOLUMES || item.volumes || 0), 0)
    const totalPeso = notasDoCarro.reduce((acc, item) => acc + Number(item.PESO || item.peso || 0), 0)
    const totalM3 = notasDoCarro.reduce((acc, item) => acc + Number(item.M3 || item.m3 || 0), 0)
    const totalClientes = new Set(notasDoCarro.map(item => item.REMETENTE || item.CLIENTE)).size
    const totalCtrcs = new Set(notasDoCarro.map(item => item.ctrc || item.cte).filter(Boolean)).size
    const totalImpressos = notasDoCarro.filter(item => Number(item.STATUS) === 1).length

    return {
      totalNotas,
      totalVolumes,
      totalPeso: totalPeso.toFixed(2),
      totalM3: totalM3.toFixed(3),
      totalClientes,
      totalCtrcs,
      totalImpressos,
      totalPendentes: totalNotas - totalImpressos
    }
  }, [notasDoCarro])

  // Disparar busca no ERP SSW
  const handleConsultarSsw = async (placaAlvo = null) => {
    const placa = (placaAlvo || carroSelecionado || '').trim()
    if (!placa) return
    try {
      setLoadingSsw(true)
      setErroSsw(null)
      setDadosSsw(null)
      const dataFormatada = selectedDate ? selectedDate.format('YYYY-MM-DD') : ''
      const resp = await notaanota.getConferenciaVeiculoSsw(placa, dataFormatada, empresa, finalDominio)

      if (resp) {
        setDadosSsw(resp)
      } else {
        setDadosSsw(null)
        setErroSsw('Nenhum dado retornado do ERP SSW para o veículo.')
      }
    } catch (err) {
      console.error('Erro ao consultar SSW 0039:', err)
      setErroSsw(err.message || 'Erro ao consultar o ERP SSW.')
    } finally {
      setLoadingSsw(false)
    }
  }

  // Seleciona o veículo e dispara automaticamente a consulta no ERP SSW
  const selecionarEConsultarVeiculo = (placa) => {
    setActiveClientPanels([])
    setActiveCtrcsByClient({})
    if (!placa) {
      setCarroSelecionado(null)
      setDadosSsw(null)
      setErroSsw(null)
      return
    }
    setCarroSelecionado(placa)
    handleConsultarSsw(placa)
  }

  // Ao expandir um cliente, garante que todos os CTRCs dele abram também expandidos
  const handleOuterCollapseChange = (keys) => {
    const keysArray = Array.isArray(keys) ? keys : (keys ? [keys] : [])
    setActiveClientPanels(keysArray)

    setActiveCtrcsByClient(prev => {
      const next = { ...prev }
      keysArray.forEach(k => {
        const clienteNome = String(k).replace(/^cli-/, '')
        const cliObj = estruturaHierarquica.find(c => c.cliente === clienteNome)
        if (cliObj) {
          next[clienteNome] = cliObj.ctrcs.map(c => `ctrc-${c.ctrc}`)
        }
      })
      return next
    })
  }

  // Colunas da tabela de notas dentro do CTRC
  const colunasNotas = [
    {
      title: 'Nota / Série',
      key: 'nota',
      width: 120,
      render: (_, record) => (
        <Space orientation="vertical" size={0}>
          <Text strong style={{ color: isDark ? '#ffffff' : '#1f1f1f' }}>
            NF {record.NOTA || record.nota}
          </Text>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            / {record.SERIE || record.serie || '1'}
          </Text>
        </Space>
      )
    },
    {
      title: 'Destinatário',
      key: 'destinatario',
      ellipsis: true,
      render: (_, record) => (
        <Space orientation="vertical" size={0}>
          <Text strong style={{ color: isDark ? '#e6f7ff' : '#003a8c' }}>
            {record.destinatario || record.DESTINATARIO || record.cddestinatario || '—'}
          </Text>
          {record.cddestinatario && (
            <Text type="secondary" style={{ fontSize: '11px' }}>
              {record.cddestinatario}
            </Text>
          )}
        </Space>
      )
    },
    {
      title: 'Local de Entrega',
      key: 'endereco',
      render: (_, record) => {
        const rua = record.rua || record.dslogradouro || ''
        const num = record.numero || record.nro || ''
        const cidade = record.cidade || record.dslocal || record.CIDADE || ''
        const uf = record.uf || record.dsuf || record.UF || ''

        return (
          <Space orientation="vertical" size={0}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <EnvironmentOutlined style={{ color: '#1890ff', fontSize: '12px' }} />
              <Text style={{ fontWeight: '500', color: isDark ? '#fff' : '#333' }}>
                {cidade}{uf ? `/${uf}` : ''}
              </Text>
            </div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {rua ? `${rua}${num ? `, nº ${num}` : ''}` : 'Endereço não informado'}
            </Text>
          </Space>
        )
      }
    },
    {
      title: 'Volumes',
      key: 'volumes',
      width: 110,
      align: 'center',
      render: (_, record) => {
        const temDivergencia = record.volumesSsw !== null && record.volumesSsw !== record.volumesLocal
        return (
          <Space orientation="vertical" size={0}>
            <Badge
              count={record.volumesLocal}
              overflowCount={9999}
              style={{
                backgroundColor: temDivergencia ? '#ff4d4f' : '#52c41a',
                color: '#fff',
                fontWeight: 'bold'
              }}
            />
            {record.volumesSsw !== null && (
              <Badge
                count={`SSW: ${record.volumesSsw}`}
                overflowCount={9999}
                style={{
                  backgroundColor: temDivergencia ? '#ff4d4f' : '#52c41a',
                  color: '#fff',
                  fontWeight: 'bold',
                  marginTop: '2px'
                }}
              />
            )}
          </Space>
        )
      }
    },
    {
      title: 'Peso (kg)',
      key: 'peso',
      width: 120,
      align: 'center',
      render: (_, record) => {
        const pesoLocal = Number(record.pesoLocal ?? record.PESO ?? record.peso ?? 0)
        const pesoSsw = record.pesoSsw !== null && record.pesoSsw !== undefined ? Number(record.pesoSsw) : null
        const temDivergencia = pesoSsw !== null && Math.abs(pesoSsw - pesoLocal) > 0.01

        return (
          <Space orientation="vertical" size={0}>
            <Badge
              count={`${pesoLocal.toFixed(2)} kg`}
              style={{
                backgroundColor: temDivergencia ? '#ff4d4f' : '#52c41a',
                color: '#fff',
                fontWeight: 'bold'
              }}
            />
            {pesoSsw !== null && (
              <Badge
                count={`SSW: ${pesoSsw.toFixed(2)} kg`}
                style={{
                  backgroundColor: temDivergencia ? '#ff4d4f' : '#52c41a',
                  color: '#fff',
                  fontWeight: 'bold',
                  marginTop: '2px'
                }}
              />
            )}
          </Space>
        )
      }
    },
    {
      title: 'M³',
      key: 'm3',
      width: 90,
      align: 'right',
      render: (_, record) => (
        <Text style={{ fontFamily: 'monospace', fontWeight: '500' }}>
          {Number(record.M3 || record.m3 || 0).toFixed(3)}
        </Text>
      )
    },
    {
      title: 'Etiqueta App',
      key: 'statusApp',
      width: 110,
      align: 'center',
      render: (_, record) => (
        <Tag color={Number(record.STATUS) === 1 ? 'success' : 'default'}>
          {Number(record.STATUS) === 1 ? 'Impressa' : 'Pendente'}
        </Tag>
      )
    },
    {
      title: 'Conferência / Status',
      key: 'conferencia',
      width: 150,
      render: (_, record) => (
        <Tooltip title={record.statusMotivo}>
          <Tag color={record.statusColor} style={{ cursor: 'pointer', fontWeight: 'bold' }}>
            {record.statusConferencia}
          </Tag>
        </Tooltip>
      )
    }
  ]

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <BarcodeOutlined style={{ color: '#1890ff', fontSize: '18px' }} />
          <span>Conferência de Expedição por Veículo</span>
        </div>
      }
      extra={
        <Space size="small">
          <Tooltip title={isFullscreen ? 'Sair da Tela Cheia (Esc)' : 'Maximizar Tela Cheia'}>
            <Button
              type={isFullscreen ? 'primary' : 'default'}
              danger={isFullscreen}
              size="small"
              icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={() => setIsFullscreen(prev => !prev)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                fontWeight: 500
              }}
            >
              {isFullscreen ? 'Sair da Tela Cheia' : 'Tela Cheia'}
            </Button>
          </Tooltip>
        </Space>
      }
      size="small"
      style={
        isFullscreen
          ? {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 2500,
            overflow: 'hidden',
            borderRadius: 0,
            backgroundColor: isDark ? '#141414' : '#ffffff',
            borderColor: isDark ? '#303030' : '#e8e8e8'
          }
          : {
            width: '100%',
            backgroundColor: isDark ? '#1f1f1f' : '#ffffff',
            borderColor: isDark ? '#434343' : '#e8e8e8'
          }
      }
      styles={{
        header: {
          backgroundColor: isDark ? '#111111' : '#fafafa',
          borderBottom: isDark ? '1px solid #333' : '1px solid #f0f0f0',
          color: isDark ? '#ffffff' : '#1f1f1f',
          position: isFullscreen ? 'sticky' : undefined,
          top: isFullscreen ? 0 : undefined,
          zIndex: isFullscreen ? 20 : undefined
        },
        body: {
          backgroundColor: isDark ? '#1f1f1f' : '#ffffff',
          padding: isMobile ? '8px' : '16px',
          height: isFullscreen ? 'calc(100vh - 50px)' : undefined,
          overflowY: isFullscreen ? 'auto' : undefined
        }
      }}
    >
      {/* Barra de Filtro de Veículo e Ação SSW */}
      <Card
        size="small"
        style={{
          marginBottom: '16px',
          backgroundColor: isDark ? '#141414' : '#f9f9f9',
          borderColor: isDark ? '#303030' : '#e6e6e6'
        }}
      >
        <Row gutter={[16, 16]} align="middle" justify="space-between">
          <Col xs={24} md={12} lg={10}>
            <Space orientation={isMobile ? 'vertical' : 'horizontal'} style={{ width: '100%' }} size="middle">
              <span style={{ fontWeight: '600', color: isDark ? '#fff' : '#333' }}>
                <CarOutlined style={{ marginRight: 6, color: '#1890ff' }} />
                Veículo:
              </span>
              <Select
                placeholder="Selecione o veículo para conferência"
                style={{ width: isMobile ? '100%' : 260 }}
                value={carroSelecionado}
                onChange={val => selecionarEConsultarVeiculo(val)}
                showSearch
                allowClear
                options={listaVeiculos.map(placa => {
                  const qtd = dadosExpedicao.filter(
                    it => (it.CARRO || it.carro || '').toUpperCase() === placa.toUpperCase()
                  ).length
                  return {
                    value: placa,
                    label: `${placa} (${qtd} NF${qtd !== 1 ? 's' : ''})`
                  }
                })}
              />
            </Space>
          </Col>

          <Col xs={24} md={12} lg={14} style={{ textAlign: isMobile ? 'left' : 'right' }}>
            <Space orientation="horizontal" size="small" wrap>
              <Button
                type="primary"
                icon={<SyncOutlined spin={loadingSsw} />}
                onClick={() => handleConsultarSsw(carroSelecionado)}
                loading={loadingSsw}
                disabled={!carroSelecionado}
                style={{
                  backgroundColor: carroSelecionado ? '#1890ff' : undefined,
                  fontWeight: '500'
                }}
              >
                Consultar ERP
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Badges de Veículos com Ocorrências no Sistema (tabela nota_veiculo_usos) */}
        {veiculosComOcorrencias.length > 0 && (
          <div
            style={{
              marginTop: '12px',
              paddingTop: '10px',
              borderTop: isDark ? '1px solid #2a2a2a' : '1px solid #f0f0f0'
            }}
          >
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '12px',
                alignItems: 'center',
                paddingTop: '4px',
                paddingBottom: '4px'
              }}
            >
              {veiculosComOcorrencias.map(v => {
                const isSelected = carroSelecionado === v.placa
                return (
                  <Badge
                    key={v.placa}
                    count={v.totalNotas}
                    overflowCount={999}
                    style={{
                      backgroundColor: isSelected
                        ? '#1890ff'
                        : (v.totalPendentes > 0 ? '#fa8c16' : '#52c41a'),
                      color: '#fff',
                      fontWeight: 'bold',
                      boxShadow: '0 0 0 1px #fff'
                    }}
                    title={`${v.placa}: ${v.totalNotas} notas, ${v.totalVolumes} vol (${v.totalImpressas} impressas, ${v.totalPendentes} pendentes)`}
                  >
                    <div
                      onClick={() => selecionarEConsultarVeiculo(v.placa)}
                      style={{
                        padding: '4px 10px',
                        backgroundColor: isSelected
                          ? (isDark ? '#177ddc' : '#e6f7ff')
                          : (isDark ? '#262626' : '#ffffff'),
                        border: isSelected
                          ? '2px solid #1890ff'
                          : (isDark ? '1px solid #434343' : '1px solid #d9d9d9'),
                        borderRadius: '6px',
                        color: isSelected
                          ? (isDark ? '#ffffff' : '#096dd9')
                          : (isDark ? '#e0e0e0' : '#333333'),
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        boxShadow: isSelected ? '0 2px 6px rgba(24, 144, 255, 0.35)' : 'none',
                        userSelect: 'none'
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = '#1890ff'
                          e.currentTarget.style.backgroundColor = isDark ? '#333333' : '#f0f7ff'
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = isDark ? '#434343' : '#d9d9d9'
                          e.currentTarget.style.backgroundColor = isDark ? '#262626' : '#ffffff'
                        }
                      }}
                    >
                      <CarOutlined style={{ fontSize: '12px', color: isSelected ? '#fff' : '#1890ff' }} />
                      <span>{v.placa}</span>
                    </div>
                  </Badge>
                )
              })}
            </div>
          </div>
        )}
      </Card>

      {/* Alerta de Erro do SSW se houver */}
      {erroSsw && (
        <Alert
          type="warning"
          showIcon
          closable
          message="Aviso do ERP SSW"
          description={erroSsw}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Se não houver veículo selecionado */}
      {!carroSelecionado ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span style={{ color: isDark ? '#888' : '#666' }}>
              Selecione um veículo acima para iniciar o confronto e conferência física das notas fiscais.
            </span>
          }
          style={{ margin: '48px 0' }}
        />
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {/* Cards de Resumo e Comparativo */}
          <Row gutter={[16, 16]}>
            {/* Card Local */}
            <Col xs={24} md={dadosSsw ? 12 : 24}>
              <Card
                size="small"
                title={`Dados no Sistema - Placa: ${carroSelecionado}`}
                bordered
                style={{
                  height: '100%',
                  backgroundColor: isDark ? '#141414' : '#f0f5ff',
                  borderColor: isDark ? '#303030' : '#adc6ff'
                }}
              >
                <Row gutter={8}>
                  <Col span={6}>
                    <Statistic
                      title={<span style={{ fontSize: '11px', color: isDark ? '#aaa' : '#666' }}>Clientes</span>}
                      value={statsLocal.totalClientes}
                      valueStyle={{ fontSize: '18px', fontWeight: 'bold', color: '#1890ff' }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title={<span style={{ fontSize: '11px', color: isDark ? '#aaa' : '#666' }}>CTRCs</span>}
                      value={statsLocal.totalCtrcs}
                      valueStyle={{ fontSize: '18px', fontWeight: 'bold', color: '#1890ff' }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title={<span style={{ fontSize: '11px', color: isDark ? '#aaa' : '#666' }}>Notas Fiscais</span>}
                      value={statsLocal.totalNotas}
                      valueStyle={{ fontSize: '18px', fontWeight: 'bold' }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title={<span style={{ fontSize: '11px', color: isDark ? '#aaa' : '#666' }}>Volumes / m³</span>}
                      value={`${statsLocal.totalVolumes} vol`}
                      suffix={<span style={{ fontSize: '12px' }}>({statsLocal.totalM3} m³)</span>}
                      valueStyle={{ fontSize: '16px', fontWeight: 'bold', color: '#52c41a' }}
                    />
                  </Col>
                </Row>
                <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <Tag color="success">Impressas: {statsLocal.totalImpressos}</Tag>
                  <Tag color={statsLocal.totalPendentes > 0 ? 'warning' : 'default'}>
                    Pendentes: {statsLocal.totalPendentes}
                  </Tag>
                </div>
              </Card>
            </Col>

            {/* Card ERP SSW (quando retornado) */}
            {dadosSsw && (
              <Col xs={24} md={12}>
                <Card
                  size="small"
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>ERP - Placa: {dadosSsw.totais?.placa || carroSelecionado}</span>
                      <Tag color="blue">{dadosSsw.totais?.dataEmissao || selectedDate?.format('DD/MM/YYYY')}</Tag>
                    </div>
                  }
                  bordered
                  style={{
                    height: '100%',
                    backgroundColor: isDark ? '#141414' : '#f6ffed',
                    borderColor: isDark ? '#303030' : '#b7eb8f'
                  }}
                >
                  <Row gutter={8}>
                    <Col span={8}>
                      <Statistic
                        title={<span style={{ fontSize: '11px', color: isDark ? '#aaa' : '#666' }}>CTRCs ERP</span>}
                        value={dadosSsw.totais?.qtdeCtrcs || (dadosSsw.itens || []).length}
                        valueStyle={{ fontSize: '18px', fontWeight: 'bold', color: '#52c41a' }}
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title={<span style={{ fontSize: '11px', color: isDark ? '#aaa' : '#666' }}>Volumes ERP</span>}
                        value={dadosSsw.totais?.qtdeVolumes || 0}
                        valueStyle={{ fontSize: '18px', fontWeight: 'bold', color: '#52c41a' }}
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title={<span style={{ fontSize: '11px', color: isDark ? '#aaa' : '#666' }}>Status ERP</span>}
                        value={
                          dadosSsw.totais?.qtdeVolumes === statsLocal.totalVolumes ? 'CONFERIDO' : 'DIVERGÊNCIA'
                        }
                        valueStyle={{
                          fontSize: '14px',
                          fontWeight: 'bold',
                          color: dadosSsw.totais?.qtdeVolumes === statsLocal.totalVolumes ? '#52c41a' : '#ff4d4f'
                        }}
                      />
                    </Col>
                  </Row>
                  <div style={{ marginTop: '8px' }}>
                    <Text type="secondary" style={{ fontSize: '11px' }}>
                      Dados extraídos diretamente da rotina do ERP.
                    </Text>
                  </div>
                </Card>
              </Col>
            )}
          </Row>

          {/* Seção de Alerta para Itens do SSW que NÃO foram localizados no App */}
          {itensSswAusentesNoApp.length > 0 && (
            <Alert
              type="error"
              showIcon
              icon={<CloseCircleOutlined />}
              message={`Atenção: Existem ${itensSswAusentesNoApp.length} nota(s) no ERP SSW que NÃO estão registradas no App para este carro:`}
              description={
                <div style={{ maxHeight: '160px', overflowY: 'auto', marginTop: 8 }}>
                  <Table
                    size="small"
                    pagination={false}
                    dataSource={itensSswAusentesNoApp}
                    rowKey={r => `${r.ctrc}-${r.nota}`}
                    columns={[
                      { title: 'CTRC', dataIndex: 'ctrc', width: 120 },
                      { title: 'NF', dataIndex: 'nota', width: 90 },
                      { title: 'Remetente', dataIndex: 'remetente', ellipsis: true },
                      { title: 'Destinatário', dataIndex: 'destinatario', ellipsis: true },
                      { title: 'Volumes', dataIndex: 'volumes', width: 80, align: 'center' },
                      { title: 'Destino', dataIndex: 'cidadeDestino', ellipsis: true },
                      {
                        title: 'Status',
                        key: 'st',
                        render: () => <Tag color="error">Pendente de Bipagem no App</Tag>
                      }
                    ]}
                  />
                </div>
              }
              style={{ marginBottom: 8 }}
            />
          )}

          {/* Hierarquia: Clientes -> CTRC -> Notas Fiscais */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0 4px 0', flexWrap: 'wrap', gap: 8 }}>
            <Title level={5} style={{ margin: 0, color: isDark ? '#ffffff' : '#333333' }}>
              Relação para Confronto Físico (Veículo: {carroSelecionado})
            </Title>
            <Space size="small">
              <Button
                size="small"
                onClick={() => {
                  const allClientKeys = estruturaHierarquica.map(c => `cli-${c.cliente}`)
                  setActiveClientPanels(allClientKeys)
                  const allCtrcsMap = {}
                  estruturaHierarquica.forEach(cli => {
                    allCtrcsMap[cli.cliente] = cli.ctrcs.map(c => `ctrc-${c.ctrc}`)
                  })
                  setActiveCtrcsByClient(allCtrcsMap)
                }}
              >
                Expandir Todos
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setActiveClientPanels([])
                  setActiveCtrcsByClient({})
                }}
              >
                Recolher Todos
              </Button>
            </Space>
          </div>

          <Collapse
            size="small"
            activeKey={activeClientPanels}
            onChange={handleOuterCollapseChange}
            style={{
              backgroundColor: isDark ? '#141414' : '#fafafa',
              borderColor: isDark ? '#303030' : '#d9d9d9'
            }}
          >
            {estruturaHierarquica.map(cli => (
              <Collapse.Panel
                key={`cli-${cli.cliente}`}
                header={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', paddingRight: '12px' }}>
                    <Space>
                      <Text strong style={{ color: isDark ? '#ffffff' : '#1f1f1f', fontSize: '13px' }}>
                        {cli.cliente}
                      </Text>
                      {cli.cnpj && (
                        <Text type="secondary" style={{ fontSize: '11px' }}>
                          ({cli.cnpj})
                        </Text>
                      )}
                    </Space>
                    <Space>
                      <Tag color="blue">{cli.ctrcs.length} CTRC{cli.ctrcs.length !== 1 ? 's' : ''}</Tag>
                      <Tag color="cyan">{cli.totalNotasCliente} NF{cli.totalNotasCliente !== 1 ? 's' : ''}</Tag>
                      <Tag color="green">{cli.totalVolumesCliente} Volumes</Tag>
                      {cli.totalPesoCliente > 0 && (
                        <Tag color="gold">{cli.totalPesoCliente.toFixed(2)} kg</Tag>
                      )}
                    </Space>
                  </div>
                }
              >
                {/* CTRCs do Cliente */}
                <Collapse
                  size="small"
                  activeKey={activeCtrcsByClient[cli.cliente] ?? cli.ctrcs.map(c => `ctrc-${c.ctrc}`)}
                  onChange={keys => {
                    const keysArray = Array.isArray(keys) ? keys : (keys ? [keys] : [])
                    setActiveCtrcsByClient(prev => ({
                      ...prev,
                      [cli.cliente]: keysArray
                    }))
                  }}
                  style={{
                    backgroundColor: isDark ? '#1f1f1f' : '#ffffff',
                    borderColor: isDark ? '#303030' : '#e8e8e8'
                  }}
                >
                  {[...cli.ctrcs].sort(compararCtrc).map(c => (
                    <Collapse.Panel
                      key={`ctrc-${c.ctrc}`}
                      header={
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', paddingRight: '12px' }}>
                          <Space>
                            <FileTextOutlined style={{ color: '#1890ff' }} />
                            <Text strong style={{ color: isDark ? '#69c0ff' : '#096dd9' }}>
                              CTRC: {c.ctrc}
                            </Text>
                          </Space>
                          <Space>
                            <Tag color="purple">Total CTRC: {c.totalVolumesCtrc} vol</Tag>
                            {c.totalPesoCtrc > 0 && (
                              <Tag color="gold">{c.totalPesoCtrc.toFixed(2)} kg</Tag>
                            )}
                            <Tag color="geekblue">{c.totalM3Ctrc.toFixed(3)} m³</Tag>
                            <Badge count={c.notas.length} style={{ backgroundColor: '#1890ff' }} />
                          </Space>
                        </div>
                      }
                    >
                      {/* Tabela de Notas Fiscais dentro do CTRC */}
                      <Table
                        columns={colunasNotas}
                        dataSource={c.notas}
                        rowKey={record => record.chave || `${record.NOTA}-${record.SERIE}`}
                        pagination={false}
                        size="small"
                        scroll={{ x: 870 }}
                        style={{
                          backgroundColor: isDark ? '#141414' : '#ffffff'
                        }}
                      />
                    </Collapse.Panel>
                  ))}
                </Collapse>
              </Collapse.Panel>
            ))}
          </Collapse>
        </Space>
      )}
    </Card>
  )
}

export default ConferenciaExpedicaoTab
